import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { formatCurrency } from '../utils/formatters';

const API = '/api/invoices';
const CLIENTS_API = '/api/clients';
const ROUTES_API = '/api/routes';
const BILLING_TYPES_API = '/api/trip-billing-types';
const UPLOAD_URL = '/api/uploads/';

/* ── Icon helper ── */
const Icon = ({ d, size = 16, sw = 1.75, color = 'currentColor' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
        stroke={color} strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round">
        {Array.isArray(d) ? d.map((p, i) => <path key={i} d={p} />) : <path d={d} />}
    </svg>
);

const ic = {
    back: 'M19 12H5M12 5l-7 7 7 7',
    save: ['M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z', 'M17 21v-8H7v8', 'M7 3v5h8'],
    plus: ['M12 5v14', 'M5 12h14'],
    trash: ['M3 6h18', 'M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2'],
    user: ['M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2', 'M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z'],
    file: ['M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z', 'M14 2v6h6'],
    list: ['M8 6h13', 'M8 12h13', 'M8 18h13', 'M3 6h.01', 'M3 12h.01', 'M3 18h.01'],
    dollar: ['M12 1v22', 'M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6'],
    check: 'M20 6 9 17l-5-5',
    info: ['M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20z', 'M12 16v-4', 'M12 8h.01'],
    bold: 'M6 4h8a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6z M6 12h9a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6z',
    italic: ['M19 4h-9', 'M14 20H5', 'M15 4L9 20'],
    ul: ['M8 6h13', 'M8 12h13', 'M8 18h13', 'M3 6h.01', 'M3 12h.01', 'M3 18h.01'],
    home: ['M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z', 'M9 22V12h6v10'],
};

const BILLING_TYPES = ['Shuttle', 'Shuttle with Booking', 'Daily', 'Ad-hoc', 'Monthly'];

/* ── Rich Text Mini Editor ── */
function RichTextArea({ value, onChange, placeholder, rows = 5 }) {
    const ref = useRef(null);
    const exec = (cmd) => { ref.current.focus(); document.execCommand(cmd, false, null); onChange(ref.current.innerHTML); };
    const handleInput = () => onChange(ref.current.innerHTML);
    useEffect(() => { if (ref.current && ref.current.innerHTML !== value) ref.current.innerHTML = value || ''; }, [value]);
    return (
        <div style={{ border: '1.5px solid #e2e8f0', borderRadius: 10, overflow: 'hidden', background: '#fff', transition: 'border-color .2s' }}
            onFocus={e => e.currentTarget.style.borderColor = '#00B2A9'}
            onBlur={e => e.currentTarget.style.borderColor = '#e2e8f0'}>
            <div style={{ display: 'flex', gap: 2, padding: '6px 10px', borderBottom: '1px solid #f1f5f9', background: '#f8fafc', alignItems: 'center' }}>
                {[['bold', 'B', { fontWeight: 800, fontSize: 13 }], ['italic', 'I', { fontStyle: 'italic', fontSize: 13 }], ['underline', 'U', { textDecoration: 'underline', fontSize: 13 }]].map(([cmd, lbl, st]) => (
                    <button key={cmd} type="button" onClick={() => exec(cmd)} title={cmd}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '3px 8px', borderRadius: 5, color: '#475569', transition: 'background .15s', ...st }}
                        onMouseEnter={e => e.currentTarget.style.background = '#e2e8f0'}
                        onMouseLeave={e => e.currentTarget.style.background = 'none'}>{lbl}</button>
                ))}
                <div style={{ width: 1, height: 16, background: '#e2e8f0', margin: '0 4px' }} />
                <button type="button" onClick={() => exec('insertUnorderedList')} title="Bullet list"
                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '3px 8px', borderRadius: 5, color: '#475569', display: 'flex', alignItems: 'center' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#e2e8f0'}
                    onMouseLeave={e => e.currentTarget.style.background = 'none'}>
                    <Icon d={ic.ul} size={13} />
                </button>
                <button type="button" onClick={() => exec('removeFormat')} title="Clear"
                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '3px 8px', borderRadius: 5, color: '#94a3b8', fontSize: 13 }}
                    onMouseEnter={e => e.currentTarget.style.background = '#e2e8f0'}
                    onMouseLeave={e => e.currentTarget.style.background = 'none'}>✕</button>
            </div>
            <div ref={ref} contentEditable suppressContentEditableWarning
                onInput={handleInput} onBlur={handleInput} data-placeholder={placeholder}
                style={{ minHeight: rows * 22, padding: '12px 14px', outline: 'none', fontSize: 14, color: '#1e293b', lineHeight: 1.6, fontFamily: 'inherit' }} />
            <style>{`[contenteditable]:empty:before{content:attr(data-placeholder);color:#94a3b8;pointer-events:none}[contenteditable] ul{padding-left:20px}[contenteditable] ol{padding-left:20px}`}</style>
        </div>
    );
}

/* ── Field component ── */
function Field({ label, required, hint, children, span }) {
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, gridColumn: span ? `span ${span}` : undefined }}>
            <label style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.07em', display: 'flex', gap: 4, alignItems: 'center' }}>
                {label}{required && <span style={{ color: '#ef4444' }}>*</span>}
                {hint && <span style={{ fontSize: 10, fontWeight: 400, color: '#94a3b8', textTransform: 'none', letterSpacing: 0 }}>— {hint}</span>}
            </label>
            {children}
        </div>
    );
}

const inp = {
    width: '100%', padding: '10px 13px', border: '1.5px solid #e2e8f0',
    borderRadius: 9, fontSize: 14, color: '#1e293b', background: '#fff',
    outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box',
    transition: 'border-color .2s, box-shadow .2s',
};

/* ── Section card ── */
function SectionCard({ num, title, subtitle, icon, children, action }) {
    return (
        <div style={{ background: '#fff', borderRadius: 18, border: '1px solid #e8ecf3', boxShadow: '0 2px 12px rgba(99,102,241,.06)', overflow: 'hidden', marginBottom: 20 }}>
            {/* Card header */}
            <div style={{ padding: '20px 28px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'linear-gradient(135deg,#fafbff,#f5f7ff)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                    <div style={{ width: 36, height: 36, borderRadius: 10, background: 'linear-gradient(135deg,#00B2A9,#16539C)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxShadow: '0 4px 10px rgba(99,102,241,.3)' }}>
                        <Icon d={icon} size={17} color="#fff" sw={2} />
                    </div>
                    <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span style={{ fontSize: 11, fontWeight: 700, color: '#00B2A9', background: '#eef2ff', padding: '2px 8px', borderRadius: 20, letterSpacing: '0.05em' }}>STEP {num}</span>
                            <h3 style={{ margin: 0, fontSize: 15, fontWeight: 800, color: '#0f172a' }}>{title}</h3>
                        </div>
                        {subtitle && <p style={{ margin: '2px 0 0', fontSize: 12, color: '#94a3b8' }}>{subtitle}</p>}
                    </div>
                </div>
                {action}
            </div>
            {/* Card body */}
            <div style={{ padding: '24px 28px' }}>{children}</div>
        </div>
    );
}

/* ── Live Preview Panel ── */
function InvoicePreview({ formData, clients, routes, totals, operatorDetails, country }) {
    const client = clients.find(c => c._id === formData.clientId);
    const { tripsSubtotal, subtotal, vatAmount, totalAmount } = totals;
    const UPLOAD = '/api/uploads/';

    return (
        <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #e2e8f0', boxShadow: '0 10px 30px rgba(0,0,0,0.08)', overflow: 'hidden', position: 'sticky', top: 24 }}>
            {/* Preview header */}
            <div style={{ padding: '14px 20px', background: '#0f172a', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 12, fontWeight: 800, color: '#f8fafc', letterSpacing: '0.08em' }}>LIVE PREVIEW</span>
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#10b981', boxShadow: '0 0 8px #10b981' }} />
            </div>

            {/* Invoice paper */}
            <div style={{ padding: '30px 24px', fontSize: 12, color: '#1e293b', lineHeight: 1.5, maxHeight: 'calc(100vh - 180px)', overflowY: 'auto', background: '#fff' }}>
                {/* Top Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 30 }}>
                    <div style={{ flex: 1 }}>
                        {operatorDetails?.logo
                            ? <img src={`${UPLOAD}${operatorDetails.logo}`} alt="Logo" style={{ maxHeight: 40, maxWidth: 140, marginBottom: 8, objectFit: 'contain' }} />
                            : <div style={{ fontSize: 20, fontWeight: 900, color: '#0f172a', letterSpacing: '-0.5px', marginBottom: 4 }}>{operatorDetails?.companyName || 'Your Company'}</div>}
                        <div style={{ fontSize: 10, color: '#64748b' }}>{operatorDetails?.address || ''}</div>
                        <div style={{ fontSize: 10, color: '#64748b' }}>{operatorDetails?.email || ''}</div>
                    </div>
                    <div style={{ textAlign: 'right', background: '#f8fafc', padding: '12px 16px', borderRadius: 8, border: '1px solid #f1f5f9' }}>
                        <div style={{ fontSize: 22, fontWeight: 900, color: '#0f172a', letterSpacing: '-0.5px', marginBottom: 8 }}>INVOICE</div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 24, fontSize: 10, color: '#475569', marginBottom: 4 }}>
                            <span style={{ fontWeight: 600 }}>Ref No:</span> <span>{formData.invoiceNumber || 'INV-XXXXX'}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 24, fontSize: 10, color: '#475569', marginBottom: 4 }}>
                            <span style={{ fontWeight: 600 }}>Issued:</span> <span>{formData.issueDate || '—'}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 24, fontSize: 10, color: '#475569' }}>
                            <span style={{ fontWeight: 600 }}>Due:</span> <span>{formData.dueDate || '—'}</span>
                        </div>
                    </div>
                </div>

                {/* Bill To & Subject Section */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 24, paddingBottom: 16, borderBottom: '1px solid #f1f5f9' }}>
                    <div>
                        <div style={{ fontSize: 9, fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 6 }}>Billed To</div>
                        {client
                            ? <><div style={{ fontWeight: 800, color: '#0f172a', fontSize: 14, marginBottom: 2 }}>{client.companyName}</div>
                                <div style={{ color: '#64748b', fontSize: 11 }}>{client.address || ''}</div>
                                <div style={{ color: '#64748b', fontSize: 11 }}>{client.contactEmail}</div></>
                            : <div style={{ color: '#94a3b8', fontStyle: 'italic', fontSize: 11 }}>Select a client…</div>}
                    </div>
                </div>

                {formData.subject && (
                    <div style={{ marginBottom: 24 }}>
                        <h4 style={{ margin: '0 0 6px 0', fontSize: 13, fontWeight: 800, color: '#0f172a' }}>{formData.subject}</h4>
                        {formData.description && <div style={{ fontSize: 11, color: '#64748b' }} dangerouslySetInnerHTML={{ __html: formData.description }}></div>}
                    </div>
                )}

                {/* Items table */}
                {formData.items.length > 0 && (
                    <div style={{ marginBottom: 24 }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '24px 1fr 60px 80px 90px', gap: '8px', padding: '10px 12px', borderBottom: '2px solid #0f172a', alignItems: 'center' }}>
                            <span style={{ fontSize: 10, fontWeight: 800, color: '#0f172a' }}>#</span>
                            <span style={{ fontSize: 10, fontWeight: 800, color: '#0f172a' }}>DESCRIPTION</span>
                            <span style={{ fontSize: 10, fontWeight: 800, color: '#0f172a', textAlign: 'right' }}>{formData.billingType === 'Ad-hoc' ? 'PASSENGERS' : 'QTY'}</span>
                            <span style={{ fontSize: 10, fontWeight: 800, color: '#0f172a', textAlign: 'right' }}>RATE</span>
                            <span style={{ fontSize: 10, fontWeight: 800, color: '#0f172a', textAlign: 'right' }}>AMOUNT</span>
                        </div>
                        {formData.items.map((item, i) => {
                            const route = routes.find(r => r._id === item.routeId);
                            const trips = (Number(item.tripsMonthly) || 0) + (Number(item.tripsDaily) || 0);
                            const qty = formData.billingType === 'Monthly' ? 1 : trips;
                            return (
                                <div key={i} style={{ display: 'grid', gridTemplateColumns: '24px 1fr 60px 80px 90px', gap: '8px', padding: '14px 12px', borderBottom: '1px solid #f1f5f9', alignItems: 'flex-start' }}>
                                    <span style={{ fontSize: 11, fontWeight: 600, color: '#64748b', paddingTop: 2 }}>{i + 1}</span>
                                    <div>
                                        <div style={{ fontSize: 12, fontWeight: 700, color: '#0f172a' }}>{item.itemName || 'Item Description'}</div>
                                        {route && <div style={{ fontSize: 10, color: '#64748b', marginTop: 3 }}>{route.name}</div>}
                                        {(!route && item.routeName) && <div style={{ fontSize: 10, color: '#64748b', marginTop: 3 }}>{item.routeName}</div>}
                                    </div>
                                    <span style={{ fontSize: 11, color: '#475569', textAlign: 'right', fontWeight: 600 }}>{qty.toFixed(2)}</span>
                                    <span style={{ fontSize: 11, color: '#475569', textAlign: 'right', fontWeight: 600 }}>{Number(item.amountPerTrip || 0).toFixed(3)}</span>
                                    <span style={{ fontSize: 12, fontWeight: 800, color: '#0f172a', textAlign: 'right' }}>{(item.totalAmount || 0).toFixed(3)}</span>
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* Totals Section */}
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 30 }}>
                    <div style={{ width: '260px' }}>
                        {[['Subtotal', tripsSubtotal], ['Additional', Number(formData.additionalAmount) || 0], ['Net Amount', subtotal], [`VAT (${formData.vatPercentage}%)`, vatAmount]].map(([lbl, val]) => (
                            <div key={lbl} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#475569', padding: '6px 0', borderBottom: '1px solid #f8fafc' }}>
                                <span style={{ fontWeight: 600 }}>{lbl}</span><span style={{ fontWeight: 700 }}>{formatCurrency(val, country)}</span>
                            </div>
                        ))}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12, padding: '12px', background: '#0f172a', color: '#fff', borderRadius: 8 }}>
                            <span style={{ fontSize: 12, fontWeight: 700 }}>TOTAL DUE</span>
                            <span style={{ fontSize: 16, fontWeight: 900 }}>{formatCurrency(totalAmount, country)}</span>
                        </div>
                    </div>
                </div>

                {/* Footer notes */}
                {formData.notes && (
                    <div style={{ padding: '16px', background: '#f8fafc', borderRadius: 8, borderLeft: '4px solid #cbd5e1' }}>
                        <div style={{ fontSize: 9, fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>Notes & Terms</div>
                        <div style={{ fontSize: 11, color: '#475569' }} dangerouslySetInnerHTML={{ __html: formData.notes }}></div>
                    </div>
                )}
            </div>
        </div>
    );
}

/* ════════════════════════════════════════════
   MAIN COMPONENT
════════════════════════════════════════════ */
export default function CreateInvoicePage({ user: propUser, operatorDetails, editItem, onBack, onSaved }) {
    const user = propUser || JSON.parse(localStorage.getItem('ops-user') || '{}');
    const operatorId = user?.operatorId || (user?.userType === 'operator' ? user._id : null);
    const country = operatorDetails?.country;

    const [clients, setClients] = useState([]);
    const [routes, setRoutes] = useState([]);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [availableBillingTypes, setAvailableBillingTypes] = useState([]);
    const [loadingBT, setLoadingBT] = useState(false);

    const [formData, setFormData] = useState({
        clientId: '',
        billingType: '',
        billingMonth: editItem?.billingMonth || '',
        invoiceNumber: editItem ? editItem.invoiceNumber : `INV-${Date.now().toString().slice(-6)}`,
        issueDate: editItem ? new Date(editItem.issueDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
        dueDate: editItem ? new Date(editItem.dueDate).toISOString().split('T')[0] : new Date(Date.now() + 7 * 86400000).toISOString().split('T')[0],
        subject: editItem?.subject || '',
        description: editItem?.description || '',
        items: editItem?.items?.length ? editItem.items : [{ itemName: '', routeId: '', routeName: '', tripsMonthly: 0, tripsDaily: 0, amountPerTrip: 0, totalAmount: 0 }],
        additionalAmount: editItem?.additionalAmount || 0,
        vatPercentage: editItem?.vatPercentage || 0,
        notes: editItem?.notes || '',
        signatureName: editItem?.signatureName || 'Authorized Signatory',
    });

    const set = (k, v) => setFormData(p => ({ ...p, [k]: v }));

    useEffect(() => {
        if (!operatorId) return;
        Promise.all([axios.get(`${CLIENTS_API}/${operatorId}`), axios.get(`${ROUTES_API}/${operatorId}`)]).then(([cR, rR]) => {
            setClients(cR.data); setRoutes(rR.data);
            if (editItem?.clientId) {
                const cId = editItem.clientId?._id || editItem.clientId;
                setFormData(p => ({ ...p, clientId: cId, billingType: editItem.billingType || '', billingMonth: editItem.billingMonth || '' }));
                fetchBT(cId);
            }
        });
    }, [operatorId]);

    const fetchBT = async (clientId) => {
        if (!clientId) { setAvailableBillingTypes([]); return; }
        setLoadingBT(true);
        try { const r = await axios.get(`${BILLING_TYPES_API}?operatorId=${operatorId}&clientId=${clientId}`); setAvailableBillingTypes(Array.isArray(r.data) ? r.data : []); }
        catch { setAvailableBillingTypes([]); }
        finally { setLoadingBT(false); }
    };

    const handleClientChange = (v) => { set('clientId', v); set('billingType', ''); set('billingMonth', ''); fetchBT(v); };

    const handleItemChange = (idx, field, value) => {
        const items = [...formData.items];
        const item = { ...items[idx] };
        if (field === 'routeId') { const r = routes.find(r => r._id === value); item.routeId = value; item.routeName = r ? r.name : ''; }
        else item[field] = value;
        const trips = (Number(item.tripsMonthly) || 0) + (Number(item.tripsDaily) || 0);
        if (formData.billingType === 'Monthly') {
            item.totalAmount = Number(item.amountPerTrip) || 0;
        } else {
            item.totalAmount = trips * (Number(item.amountPerTrip) || 0);
        }
        items[idx] = item;
        setFormData(p => ({ ...p, items }));
    };

    const addItem = () => setFormData(p => ({ ...p, items: [...p.items, { itemName: '', routeId: '', routeName: '', tripsMonthly: 0, tripsDaily: 0, amountPerTrip: 0, totalAmount: 0 }] }));
    const removeItem = (i) => { const items = [...formData.items]; items.splice(i, 1); setFormData(p => ({ ...p, items })); };

    const [prefilling, setPrefilling] = useState(false);
    const handlePrefill = async () => {
        if (!formData.clientId || !formData.billingType || !formData.billingMonth) {
            setError('Please select Client, Billing Type, and Billing Month to prefill.');
            return;
        }
        setPrefilling(true);
        setError('');
        try {
            const res = await axios.get(`${API}/prefill/${formData.clientId}?billingType=${formData.billingType}&billingMonth=${formData.billingMonth}`);
            if (res.data.items && res.data.items.length > 0) {
                setFormData(p => ({ ...p, items: res.data.items }));
            } else {
                setError('No completed trips found for the selected criteria.');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to prefill trips.');
        } finally {
            setPrefilling(false);
        }
    };

    const calcTotals = () => {
        const tripsSubtotal = formData.items.reduce((s, it) => s + (Number(it.totalAmount) || 0), 0);
        const subtotal = tripsSubtotal + (Number(formData.additionalAmount) || 0);
        const vatAmount = subtotal * (Number(formData.vatPercentage) / 100);
        return { tripsSubtotal, subtotal, vatAmount, totalAmount: subtotal + vatAmount };
    };
    const totals = calcTotals();

    const handleSave = async (e) => {
        e.preventDefault();
        if (!formData.clientId) { setError('Please select a client to continue.'); return; }
        setSaving(true); setError('');
        try {
            const data = { ...formData, operatorId, ...totals };
            if (editItem) await axios.put(`${API}/${editItem._id}`, data);
            else await axios.post(API, data);
            onSaved?.();
        } catch { setError('Failed to save invoice. Please try again.'); }
        finally { setSaving(false); }
    };

    const inputStyle = (focused) => ({
        ...inp,
        borderColor: focused ? '#00B2A9' : '#e2e8f0',
        boxShadow: focused ? '0 0 0 3px rgba(99,102,241,.12)' : 'none',
    });

    const FocusInput = ({ type = 'text', ...props }) => {
        const [f, setF] = useState(false);
        return <input type={type} {...props} style={{ ...inp, borderColor: f ? '#00B2A9' : '#e2e8f0', boxShadow: f ? '0 0 0 3px rgba(99,102,241,.12)' : 'none' }}
            onFocus={() => setF(true)} onBlur={() => setF(false)} />;
    };
    const FocusSelect = ({ children, ...props }) => {
        const [f, setF] = useState(false);
        return <select {...props} style={{ ...inp, height: 42, borderColor: f ? '#00B2A9' : '#e2e8f0', boxShadow: f ? '0 0 0 3px rgba(99,102,241,.12)' : 'none', cursor: 'pointer' }}
            onFocus={() => setF(true)} onBlur={() => setF(false)}>{children}</select>;
    };

    return (
        <div style={{ background: '#f3f4f8', minHeight: '100vh', fontFamily: "'Inter','Segoe UI',sans-serif" }}>

            {/* ── Top header bar ── */}
            <div style={{ background: '#fff', borderBottom: '1px solid #e8ecf3', padding: '0 32px', position: 'sticky', top: 0, zIndex: 100, boxShadow: '0 2px 12px rgba(0,0,0,.04)' }}>
                {/* Breadcrumb */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#94a3b8', padding: '10px 0 0' }}>
                    <Icon d={ic.home} size={14} color="#64748b" />
                    <span style={{ color: '#cbd5e1' }}>›</span>
                    <span>Finance</span>
                    <span style={{ color: '#cbd5e1' }}>›</span>
                    <button onClick={onBack} style={{ background: 'none', border: 'none', color: '#00B2A9', cursor: 'pointer', padding: 0, fontSize: 12, fontWeight: 600 }}>Invoices</button>
                    <span style={{ color: '#cbd5e1' }}>›</span>
                    <span style={{ color: '#1e293b', fontWeight: 600 }}>{editItem ? 'Edit Invoice' : 'Create Invoice'}</span>
                </div>

                {/* Header row */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 0' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                        <button onClick={onBack}
                            style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: '1.5px solid #e2e8f0', borderRadius: 8, color: '#64748b', cursor: 'pointer', padding: '7px 14px', fontSize: 13, fontWeight: 600, transition: 'all .15s' }}
                            onMouseEnter={e => { e.currentTarget.style.borderColor = '#00B2A9'; e.currentTarget.style.color = '#00B2A9'; }}
                            onMouseLeave={e => { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.color = '#64748b'; }}>
                            <Icon d={ic.back} size={15} />
                            Back
                        </button>
                        <div style={{ width: 1, height: 28, background: '#e2e8f0' }} />
                        <div>
                            <h1 style={{ margin: 0, fontSize: 20, fontWeight: 900, color: '#0f172a', letterSpacing: '-0.3px' }}>
                                {editItem ? 'Edit Invoice' : 'Create New Invoice'}
                            </h1>
                            <p style={{ margin: '2px 0 0', fontSize: 12, color: '#94a3b8' }}>Fill in all sections below — preview updates in real time</p>
                        </div>
                    </div>

                    {/* Step pills + save */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                        <div style={{ display: 'flex', gap: 8 }}>
                            {[1, 2, 3, 4, 5].map(n => (
                                <div key={n} style={{ width: 28, height: 28, borderRadius: '50%', background: 'linear-gradient(135deg,#00B2A9,#16539C)', color: '#fff', fontSize: 11, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px rgba(99,102,241,.3)' }}>{n}</div>
                            ))}
                        </div>
                        <button onClick={handleSave} disabled={saving}
                            style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'linear-gradient(135deg,#00B2A9,#16539C)', color: '#fff', border: 'none', borderRadius: 10, padding: '10px 22px', fontSize: 14, fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer', boxShadow: '0 4px 14px rgba(99,102,241,.35)', opacity: saving ? 0.7 : 1, transition: 'all .2s' }}>
                            <Icon d={ic.save} size={15} color="#fff" />
                            {saving ? 'Saving…' : editItem ? 'Update Invoice' : 'Save Invoice'}
                        </button>
                    </div>
                </div>
            </div>

            {/* ── Error banner ── */}
            {error && (
                <div style={{ margin: '16px 32px 0', background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', borderRadius: 10, padding: '12px 16px', fontSize: 14, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 10 }}>
                    <Icon d={ic.info} size={16} color="#dc2626" />
                    {error}
                </div>
            )}

            {/* ── Main content: left form + right preview ── */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: 24, padding: '24px 32px', maxWidth: 1400, margin: '0 auto' }}>

                {/* LEFT: Form */}
                <form onSubmit={handleSave}>

                    {/* ── Section 1: Client & Invoice Info ── */}
                    <SectionCard num={1} title="Client & Invoice Details" subtitle="Select client, billing type, invoice number and dates" icon={ic.user}>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 16 }}>
                            <Field label="Client" required>
                                <FocusSelect value={formData.clientId} onChange={e => handleClientChange(e.target.value)} required>
                                    <option value="">Select a Client</option>
                                    {clients.map(c => <option key={c._id} value={c._id}>{c.companyName}</option>)}
                                </FocusSelect>
                            </Field>

                            <Field label={loadingBT ? 'Billing Type (loading…)' : 'Billing Type'} required>
                                <FocusSelect value={formData.billingType} onChange={e => set('billingType', e.target.value)} disabled={!formData.clientId}>
                                    <option value="">All / Not Specified</option>
                                    {(availableBillingTypes.length > 0 ? availableBillingTypes : BILLING_TYPES).map(bt => <option key={bt} value={bt}>{bt}</option>)}
                                </FocusSelect>
                                {formData.clientId && availableBillingTypes.length === 0 && !loadingBT && (
                                    <span style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>No trip data found — showing defaults</span>
                                )}
                            </Field>

                            <Field label="Billing Month" required hint="Required to auto-fetch trips">
                                <FocusInput type="month" value={formData.billingMonth} onChange={e => set('billingMonth', e.target.value)} required />
                            </Field>

                            <Field label="Invoice Number" required>
                                <FocusInput value={formData.invoiceNumber} onChange={e => set('invoiceNumber', e.target.value)} required />
                            </Field>

                            <Field label="Issue Date" required>
                                <FocusInput type="date" value={formData.issueDate} onChange={e => set('issueDate', e.target.value)} required />
                            </Field>

                            <Field label="Due Date" required span={2}>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                                    <FocusInput type="date" value={formData.dueDate} onChange={e => set('dueDate', e.target.value)} required />
                                    {/* Payment terms badge */}
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '0 14px', background: '#f5f7ff', borderRadius: 9, border: '1.5px solid #e6f0fa' }}>
                                        <Icon d={ic.info} size={14} color="#00B2A9" />
                                        <span style={{ fontSize: 12, color: '#16539C', fontWeight: 600 }}>
                                            {formData.issueDate && formData.dueDate
                                                ? `${Math.round((new Date(formData.dueDate) - new Date(formData.issueDate)) / 86400000)} day payment terms`
                                                : 'Set both dates to see terms'}
                                        </span>
                                    </div>
                                </div>
                            </Field>
                        </div>
                    </SectionCard>

                    {/* ── Section 2: Subject & Description ── */}
                    <SectionCard num={2} title="Subject & Description" subtitle="These appear prominently on the invoice PDF" icon={ic.file}>
                        <Field label="Subject" hint="appears as the invoice title">
                            <FocusInput
                                value={formData.subject}
                                onChange={e => set('subject', e.target.value)}
                                placeholder="e.g. Monthly Transportation Services – April 2026"
                            />
                        </Field>
                        <div style={{ marginTop: 16 }}>
                            <Field label="Description / Invoice Body" hint="rich text — supports bold, lists etc.">
                                <RichTextArea
                                    value={formData.description}
                                    onChange={v => set('description', v)}
                                    placeholder="Detailed opening text that appears above the items table in the PDF…"
                                    rows={6}
                                />
                            </Field>
                        </div>
                    </SectionCard>

                    {/* ── Section 3: Invoice Line Items ── */}
                    <SectionCard num={3} title="Invoice Line Items" subtitle="Add one row per route — amounts calculate automatically"
                        icon={ic.list}
                        action={
                            <div style={{ display: 'flex', gap: 10 }}>
                                <button type="button" onClick={handlePrefill} disabled={prefilling || !formData.clientId || !formData.billingType || !formData.billingMonth}
                                    style={{ display: 'flex', alignItems: 'center', gap: 7, background: '#fef3c7', border: '1px solid #fde68a', borderRadius: 8, color: '#d97706', fontSize: 12, fontWeight: 700, cursor: (prefilling || !formData.clientId || !formData.billingType || !formData.billingMonth) ? 'not-allowed' : 'pointer', padding: '7px 14px', transition: 'all .15s', opacity: (prefilling || !formData.clientId || !formData.billingType || !formData.billingMonth) ? 0.6 : 1 }}>
                                    <Icon d={ic.list} size={13} color="#d97706" /> {prefilling ? 'Fetching...' : 'Auto Prefill'}
                                </button>
                                <button type="button" onClick={addItem}
                                    style={{ display: 'flex', alignItems: 'center', gap: 7, background: '#eef2ff', border: '1px solid #c3dcf5', borderRadius: 8, color: '#16539C', fontSize: 12, fontWeight: 700, cursor: 'pointer', padding: '7px 14px', transition: 'all .15s' }}
                                    onMouseEnter={e => e.currentTarget.style.background = '#e6f0fa'}
                                    onMouseLeave={e => e.currentTarget.style.background = '#eef2ff'}>
                                    <Icon d={ic.plus} size={13} color="#16539C" /> Add Route
                                </button>
                            </div>
                        }>
                        <div style={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: '0 6px', fontSize: 13 }}>
                                <thead>
                                    <tr>
                                        {['Item Name', 'Route', formData.billingType === 'Ad-hoc' ? 'No. of Passengers' : 'Qty / Trips', 'Rate', 'Amount', ''].map(h => (
                                            <th key={h} style={{ padding: '8px 10px', fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.07em', textAlign: h === 'Amount' ? 'right' : 'left', borderBottom: '2px solid #f1f5f9' }}>{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {formData.items.map((item, idx) => (
                                        <tr key={idx} style={{ background: idx % 2 === 0 ? '#fafbff' : '#fff' }}>
                                            <td style={{ padding: '8px 6px', borderRadius: '8px 0 0 8px' }}>
                                                <FocusInput type="text" value={item.itemName || ''} onChange={e => handleItemChange(idx, 'itemName', e.target.value)} placeholder="e.g. Staff Transport" style={{ width: 140 }} />
                                            </td>
                                            <td style={{ padding: '8px 6px' }}>
                                                <FocusSelect value={item.routeId} onChange={e => handleItemChange(idx, 'routeId', e.target.value)} required style={{ minWidth: 170 }}>
                                                    <option value="">Select Route</option>
                                                    {routes.map(r => <option key={r._id} value={r._id}>{r.name}</option>)}
                                                </FocusSelect>
                                            </td>
                                            <td style={{ padding: '8px 6px' }}>
                                                <FocusInput type="number" min="0" value={(Number(item.tripsMonthly) || 0) + (Number(item.tripsDaily) || 0)}
                                                    onChange={e => {
                                                        const val = Number(e.target.value) || 0;
                                                        handleItemChange(idx, 'tripsMonthly', val);
                                                        handleItemChange(idx, 'tripsDaily', 0);
                                                    }}
                                                    style={{ width: 80 }} />
                                            </td>
                                            <td style={{ padding: '8px 6px' }}>
                                                <FocusInput type="number" min="0" step="0.001" value={item.amountPerTrip}
                                                    onChange={e => handleItemChange(idx, 'amountPerTrip', e.target.value)}
                                                    style={{ width: 100 }} />
                                            </td>
                                            <td style={{ padding: '8px 6px', textAlign: 'right', fontWeight: 800, color: '#16539C', fontSize: 14 }}>
                                                {formatCurrency(item.totalAmount || 0, country)}
                                            </td>
                                            <td style={{ padding: '8px 6px', borderRadius: '0 8px 8px 0', textAlign: 'center' }}>
                                                {formData.items.length > 1 && (
                                                    <button type="button" onClick={() => removeItem(idx)}
                                                        style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, color: '#ef4444', cursor: 'pointer', padding: '5px 8px', display: 'flex', alignItems: 'center', transition: 'all .15s' }}
                                                        onMouseEnter={e => e.currentTarget.style.background = '#fee2e2'}
                                                        onMouseLeave={e => e.currentTarget.style.background = '#fef2f2'}>
                                                        <Icon d={ic.trash} size={13} color="#ef4444" />
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </SectionCard>

                    {/* ── Section 4: Charges & Totals ── */}
                    <SectionCard num={4} title="Charges & Totals" subtitle="Additional fees, VAT and grand total" icon={ic.dollar}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, alignItems: 'start' }}>
                            {/* Inputs */}
                            <div style={{ display: 'grid', gap: 16 }}>
                                <Field label="Additional Amount" hint="optional surcharge">
                                    <FocusInput type="number" step="0.001" min="0" placeholder="0.00"
                                        value={formData.additionalAmount} onChange={e => set('additionalAmount', e.target.value)} />
                                </Field>
                                <Field label="VAT (%)">
                                    <FocusSelect value={formData.vatPercentage} onChange={e => set('vatPercentage', e.target.value)}>
                                        {[0, 5, 10, 15, 18, 20].map(v => <option key={v} value={v}>{v}%</option>)}
                                    </FocusSelect>
                                </Field>
                            </div>
                            {/* Totals display */}
                            <div style={{ background: 'linear-gradient(135deg,#f5f7ff,#eef2ff)', borderRadius: 14, padding: '20px 22px', border: '1px solid #e6f0fa' }}>
                                {[['Trips Subtotal', totals.tripsSubtotal], ['Additional Amount', Number(formData.additionalAmount) || 0], ['Subtotal', totals.subtotal, true], [`VAT (${formData.vatPercentage}%)`, totals.vatAmount]].map(([lbl, val, bold]) => (
                                    <div key={lbl} style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 0', borderBottom: '1px solid #e6f0fa', fontSize: 13, color: bold ? '#0f172a' : '#475569', fontWeight: bold ? 800 : 400 }}>
                                        <span>{lbl}</span>
                                        <span>{formatCurrency(val, country)}</span>
                                    </div>
                                ))}
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 12, paddingTop: 10, borderTop: '2.5px solid #00B2A9' }}>
                                    <span style={{ fontSize: 16, fontWeight: 900, color: '#0f172a' }}>Grand Total</span>
                                    <span style={{ fontSize: 20, fontWeight: 900, color: '#00B2A9' }}>{formatCurrency(totals.totalAmount, country)}</span>
                                </div>
                            </div>
                        </div>
                    </SectionCard>

                    {/* ── Section 5: Notes & Signatory ── */}
                    <SectionCard num={5} title="Notes & Authorization" subtitle="Payment terms, disclaimers and authorized signatory" icon={ic.check}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
                            <Field label="Notes / Terms" hint="rich text — bold, lists etc.">
                                <RichTextArea
                                    value={formData.notes}
                                    onChange={v => set('notes', v)}
                                    placeholder="e.g. Thank you for your business. Please transfer within 30 days…"
                                    rows={6}
                                />
                            </Field>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                                <Field label="Authorized Signatory Name">
                                    <FocusInput value={formData.signatureName} onChange={e => set('signatureName', e.target.value)} placeholder="e.g. John Smith" />
                                </Field>
                                {/* Signature preview box */}
                                <div style={{ flex: 1, border: '1.5px dashed #c3dcf5', borderRadius: 10, padding: '16px', background: '#fafbff', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
                                    <div style={{ borderTop: '1.5px solid #1e293b', paddingTop: 8, textAlign: 'center' }}>
                                        <div style={{ fontSize: 12, fontWeight: 700, color: '#1e293b' }}>{formData.signatureName || 'Authorized Signatory'}</div>
                                        <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 2 }}>Signature</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </SectionCard>

                    {/* ── Footer action bar ── */}
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, paddingBottom: 40 }}>
                        <button type="button" onClick={onBack}
                            style={{ background: '#fff', border: '1.5px solid #e2e8f0', borderRadius: 10, color: '#475569', fontSize: 14, fontWeight: 600, cursor: 'pointer', padding: '11px 26px', transition: 'all .15s' }}
                            onMouseEnter={e => e.currentTarget.style.borderColor = '#94a3b8'}
                            onMouseLeave={e => e.currentTarget.style.borderColor = '#e2e8f0'}>Cancel</button>
                        <button type="submit" disabled={saving}
                            style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'linear-gradient(135deg,#00B2A9,#16539C)', color: '#fff', border: 'none', borderRadius: 10, padding: '11px 28px', fontSize: 14, fontWeight: 800, cursor: saving ? 'not-allowed' : 'pointer', boxShadow: '0 4px 14px rgba(99,102,241,.35)', opacity: saving ? 0.7 : 1, transition: 'all .2s' }}>
                            <Icon d={ic.save} size={15} color="#fff" />
                            {saving ? 'Saving…' : editItem ? 'Update Invoice' : 'Create Invoice'}
                        </button>
                    </div>
                </form>

                {/* RIGHT: Live Preview */}
                <div>
                    <InvoicePreview
                        formData={formData}
                        clients={clients}
                        routes={routes}
                        totals={totals}
                        operatorDetails={operatorDetails}
                        country={country}
                    />
                </div>
            </div>
        </div>
    );
}
